import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

interface Props {
  userId: string;
}

interface Signals {
  blogs: number;
  social_channels: number;
  posts: number;
  contacts: number;
  sent_campaigns: number;
  last_login: string | null;
  has_first_blog: boolean;
  has_first_post: boolean;
  has_first_newsletter: boolean;
}

export function JourneySignalsBlock({ userId }: Props) {
  const [signals, setSignals] = useState<Signals | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [blogs, posts, contacts, campaigns, user, socialProfile] = await Promise.all([
        supabase.from('published_blogs').select('id', { count: 'exact', head: true }).eq('user_id', userId),
        supabase.from('social_media_posts').select('id', { count: 'exact', head: true }).eq('user_id', userId),
        supabase.from('email_contacts').select('id', { count: 'exact', head: true }).eq('user_id', userId),
        supabase.from('email_campaigns').select('id', { count: 'exact', head: true }).eq('user_id', userId).eq('status', 'completed'),
        supabase.from('users').select('last_login_at, last_sign_in_at').eq('id', userId).maybeSingle(),
        supabase.from('social_profiles').select('connected_platforms').eq('user_id', userId).maybeSingle(),
      ]);
      if (cancelled) return;
      const u = (user.data ?? {}) as any;
      const connected = (socialProfile.data?.connected_platforms ?? {}) as Record<string, { handle?: string; reauth_required?: boolean } | '' | undefined>;
      const channelCount = ['facebook', 'linkedin', 'instagram'].filter((p) => {
        const a = connected[p];
        return !!(a && typeof a === 'object' && a.handle && !a.reauth_required);
      }).length;
      setSignals({
        blogs: blogs.count ?? 0,
        social_channels: channelCount,
        posts: posts.count ?? 0,
        contacts: contacts.count ?? 0,
        sent_campaigns: campaigns.count ?? 0,
        last_login: u.last_login_at ?? u.last_sign_in_at ?? null,
        has_first_blog: (blogs.count ?? 0) > 0,
        has_first_post: (posts.count ?? 0) > 0,
        has_first_newsletter: (campaigns.count ?? 0) > 0,
      });
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (loading) return <div className="bg-white rounded-lg border border-slate-200 p-4 text-sm text-slate-500">Signalen laden...</div>;
  if (!signals) return null;

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <h3 className="text-sm font-semibold text-slate-700 mb-3">Signalen</h3>
      <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
        <Row label="Blogs gepubliceerd" value={signals.blogs} milestone={signals.has_first_blog ? 'eerste blog ✓' : 'nog niet'} />
        <Row label="Social kanalen" value={signals.social_channels} milestone={signals.social_channels > 0 ? 'gekoppeld' : 'geen'} />
        <Row label="Social posts" value={signals.posts} milestone={signals.has_first_post ? 'eerste post ✓' : 'nog niet'} />
        <Row label="Mailinglijst contacten" value={signals.contacts} milestone={signals.contacts > 0 ? 'lijst aanwezig' : 'leeg'} />
        <Row label="Verzonden nieuwsbrieven" value={signals.sent_campaigns} milestone={signals.has_first_newsletter ? 'eerste nieuwsbrief ✓' : 'nog niet'} />
        <Row label="Laatste login" value={signals.last_login ? new Date(signals.last_login).toLocaleDateString('nl-NL') : '—'} milestone="" />
      </div>
    </div>
  );
}

function Row({ label, value, milestone }: { label: string; value: number | string; milestone: string }) {
  return (
    <>
      <div className="text-slate-600">{label}</div>
      <div className="font-medium text-slate-900 flex items-baseline gap-2">
        <span>{value}</span>
        {milestone && <span className="text-xs text-slate-500">{milestone}</span>}
      </div>
    </>
  );
}
